// Static registry mapping a Source brand id (the value carried on
// Provider.Source and Work.Source server-side) to its user-facing
// display metadata: a human-readable name, the canonical domain for
// the favicon lookup, and a CSS color class for the chip background.
//
// This is intentionally a small static table rather than a backend
// entity. The number of brands is bounded (one per publisher we
// crawl) and changes slowly; adding a row here in lockstep with a
// new IPackageSource implementation is the natural seam. Promote to
// a real `Source` entity later if it grows fields the registry can't
// carry.

export interface SourceMeta {
    /** User-facing label rendered on cards / chips ("Ko-Fi"). */
    name: string;
    /** Canonical hostname used by Google's favicon service. */
    domain: string;
    /** Tailwind class for the chip / fallback square background. */
    color: string;
}

const REGISTRY: Record<string, SourceMeta> = {
    'ko-fi':         { name: 'Ko-Fi',           domain: 'ko-fi.com',           color: 'bg-source-ko-fi' },
    nexusmods:       { name: 'NexusMods',       domain: 'www.nexusmods.com',   color: 'bg-source-nexusmods' },
    gamebanana:      { name: 'GameBanana',      domain: 'gamebanana.com',      color: 'bg-source-gamebanana' },
    xivmodarchive:   { name: 'XIV Mod Archive', domain: 'www.xivmodarchive.com', color: 'bg-source-xivmodarchive' },
    aetherlink:      { name: 'AetherLink',      domain: 'aetherlink.app',      color: 'bg-source-aetherlink' },
    lazynet:         { name: 'LazyNet',         domain: 'sfx.thelazy.net',     color: 'bg-source-lazynet' },
    gumroad:         { name: 'Gumroad',         domain: 'gumroad.com',         color: 'bg-source-gumroad' },
    github:          { name: 'GitHub',          domain: 'github.com',          color: 'bg-source-github' },
    discord:         { name: 'Discord',         domain: 'discord.com',         color: 'bg-source-discord' },
    'glamour-dresser': { name: 'Glamour Dresser', domain: 'www.glamourdresser.com', color: 'bg-source-glamour-dresser' },
    direct:          { name: 'Direct',          domain: '',                    color: 'bg-source-direct' },
};

/**
 * Resolve a brand id to its display metadata. Falls back to a
 * title-cased version of the id when the brand isn't registered, so
 * a brand-new Provider variant still renders something readable
 * before someone adds the proper row.
 */
export function resolveSource(source: string | null | undefined): SourceMeta {
    if (!source) return { name: 'Unknown', domain: '', color: 'bg-fg-muted' };
    const hit = REGISTRY[source];
    if (hit) return hit;
    return {
        name: titleCase(source),
        domain: '',
        color: 'bg-fg-muted',
    };
}

/** All known brand ids. Useful for admin / debug surfaces. */
export function listKnownSources(): string[] {
    return Object.keys(REGISTRY).sort();
}

function titleCase(input: string): string {
    return input
        .split(/[-\s]+/)
        .map((p) => (p.length === 0 ? p : p[0]!.toUpperCase() + p.slice(1)))
        .join(' ');
}
